import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  HStack,
  VStack,
  Flex,
  Box,
  Text as ChakraText,
  Link,
  Spacer,
  Icon,
  Table,
  Tr,
  Thead,
  Tbody,
  Th,
  Td,
  Center,
  Spinner,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { Interceptors } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import { NavLink, useParams } from 'react-router-dom'
import { useSortBy, useTable } from 'react-table'
import type { Column } from 'react-table'
import moment from 'moment'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import numeral from 'numeral'
import { Bar } from 'react-chartjs-2'
import type { ChartOptions } from 'chart.js'
import { colors } from '../../../ui-kit'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import type {
  PerformanceReport,
  PerformanceReportResponse,
} from '../../interfaces/Performance'
import {
  CorrectIcon,
  DangerIcon,
} from '../../components/Performance/Suggestions'
import FreePlanCTA from '../../components/FreePlan'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'

const PerformanceSummaryLayout = (): React.ReactElement | null => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop'>('desktop')

  const { urlId } = useParams<{
    urlId?: string
  }>()
  const interceptors: Interceptors<PerformanceReportResponse[]> = {
    response: async ({ response }) =>
      // if (response.data) {
      //   response.data.mobile.createdAt = response.data.createdAt
      //   response.data.desktop.createdAt = response.data.createdAt
      // }
      response,
  }

  const TableHeader = useMemo(
    () =>
      ({
        translationKey,
        mainText,
      }: {
        translationKey: string
        mainText: string
      }): React.ReactElement =>
        (
          <ChakraText color={colors.appDarkIndigo} fontWeight="medium">
            {t(translationKey, mainText)}
          </ChakraText>
        ),
    [],
  )

  const columns: Array<Column<PerformanceReportResponse>> = useMemo(
    () => [
      {
        id: `${deviceType}.pageUrl`,
        accessor: row => row[deviceType].pageUrl,
        Header: <TableHeader translationKey="url" mainText="URL" />,
        Cell: ({ value }) => <ChakraText color="#2d3748"> {value} </ChakraText>,
      },
      {
        id: 'createdAt',
        Header: <TableHeader translationKey="date" mainText="Date" />,
        accessor: 'createdAt',
        Cell: ({ value }) => (
          <ChakraText color="#2d3748">
            {' '}
            {moment(value).format('D MMM YY')}{' '}
          </ChakraText>
        ),
      },
      {
        id: `${deviceType}.totalImageLoaded`,
        Header: (
          <TableHeader translationKey="image_count" mainText="Image Count" />
        ),
        accessor: row => row[deviceType].totalImageLoaded,
        Cell: ({ value }) => <ChakraText color="#2d3748"> {value} </ChakraText>,
      },
      {
        id: `${deviceType}.usingLazyLoading`,
        Header: (
          <TableHeader translationKey="lazy_loading" mainText="Lazy Loading" />
        ),
        accessor: row => row[deviceType].usingLazyLoading,
        Cell: ({
          row,
          value,
        }: {
          row: { original: PerformanceReportResponse }
          value: boolean
        }) =>
          !value ? (
            <Popover placement="top" trigger="hover">
              <PopoverTrigger>
                <DangerIcon />
              </PopoverTrigger>
              <PopoverContent>
                {`${row.original[deviceType].noOfUrlsBeforeScroll} images loaded on page load`}
              </PopoverContent>
            </Popover>
          ) : (
            <CorrectIcon />
          ),
      },
      {
        id: `${deviceType}.usingHTTP2`,
        Header: (
          <TableHeader translationKey="using_http2" mainText="Using HTTP/2" />
        ),
        accessor: row => row[deviceType].usingHTTP2,
        Cell: ({
          row,
          value,
        }: {
          row: { original: PerformanceReportResponse }
          value: boolean
        }) =>
          !value ? (
            <Popover placement="top" trigger="hover">
              <PopoverTrigger>
                <DangerIcon />
              </PopoverTrigger>
              <PopoverContent>{`${row.original[deviceType].noOfUrlsBeforeScroll} images loaded on page load`}</PopoverContent>
            </Popover>
          ) : (
            <CorrectIcon />
          ),
      },
      {
        id: `${deviceType}.totalImageSize`,
        Header: (
          <TableHeader
            translationKey="image_payload"
            mainText="Image Payload"
          />
        ),
        accessor: row => row[deviceType].totalImageSize,
        Cell: ({ value }) => (
          <ChakraText color="#2d3748">
            {' '}
            {numeral(value).format('0.0 b')}{' '}
          </ChakraText>
        ),
      },
      {
        id: `${deviceType}.savings`,
        Header: <TableHeader translationKey="savings" mainText="Savings" />,
        accessor: row =>
          `${row[deviceType].totalImagePercentSaving}% (${numeral(
            row[deviceType].savings,
          ).format('0.0 b')})`,
        Cell: ({ value }) => (
          <HStack>
            <ChakraText color="#2d3748"> {value} </ChakraText>
          </HStack>
        ),
      },
      {
        id: 'reportLink',
        Header: <></>,
        accessor: '_id',
        Cell: ({ row }) => (
          <Link
            as={NavLink}
            to={`/performance-alerts/report/${deviceType}/${row.original._id}`}
          >
            <ChakraText color={colors.appClearBlue}>
              {row.original[deviceType].savings > 100000
                ? t('fix_it', 'Fix it')
                : t('view_more', 'View more')}
            </ChakraText>
          </Link>
        ),
      },
    ],
    [deviceType],
  )

  const {
    get: getSummaryFetch,
    data: summary,
    loading,
  } = useFetch<PerformanceReportResponse[]>({
    interceptors,
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  useEffect(() => {
    if (
      urlId &&
      user?.pricingPlan.featuresList.includes(FeaturesEnum.PERFORMANCE_CENTER)
    ) {
      const startDate = moment()
        .subtract(360, 'day')
        .startOf('day')
        .format('DDMMYYYY')
      const endDate = moment().endOf('day').format('DDMMYYYY')
      const getSummary = async (): Promise<void> => {
        await getSummaryFetch(
          `/api/performance/${user.clientNumber}/summary/${urlId}?startDate=${startDate}&endDate=${endDate}`,
        )
      }
      void getSummary()
    }
  }, [user])

  const filteredItems = useMemo(
    () =>
      summary?.filter(item => {
        if (item[deviceType]) {
          return true
        }
        return false
      }) ?? [],
    [summary, deviceType],
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: filteredItems ?? [] }, useSortBy)

  const graphData = useMemo(() => {
    const graphLabels = [] as string[]
    const optimizedDataPoints = [] as number[]
    const unOptimizedDataPoints = [] as number[]
    void filteredItems.forEach(item => {
      const report: PerformanceReport = item[deviceType]
      graphLabels.push(item.createdAt)
      optimizedDataPoints.push(report.totalOptimizedImageSize)
      unOptimizedDataPoints.push(report.savings)
    })

    return {
      labels: graphLabels,
      datasets: [
        {
          label: t('optimized_image_payload', 'Optimized image payload'),
          backgroundColor: colors.appGreenishTeal,
          data: optimizedDataPoints,
        },
        {
          label: t('unoptimized_image_payload', 'Unoptimized image payload'),
          backgroundColor: '#e5635e',
          data: unOptimizedDataPoints,
        },
      ],
    }
  }, [filteredItems, deviceType])

  const graphOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      plugins: {
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: context =>
              numeral(context.raw).format('0.0 ib').replace('i', ''),
          },
        },
      },
      responsive: true,
      scales: {
        x: {
          stacked: true,
          ticks: {
            callback: index =>
              moment(graphData.labels[index as number]).format('D MMM'),
          },
        },
        y: {
          stacked: true,
          ticks: {
            suggestedMin: 0,
            callback: value => numeral(value).format('0.0 ib').replace('i', ''),
          },
        },
      },
    }),
    [graphData],
  )

  if (!summary) {
    return null
  }

  return (
    <React.Fragment>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="semibold"
          color={colors.appDarkIndigo}
        >
          {t(
            'detailed_performance_report',
            'Detailed Image Performance Report',
          )}
        </Heading>
      </MainContentHeadingBox>

      <MainContentWrapper>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.PERFORMANCE_CENTER,
        ) ? (
          !loading ? (
            <VStack width="full" spacing={12} alignItems="start">
              <Flex
                width="full"
                flexDirection="row"
                borderBottom={`1px solid ${colors.appPaleLilac}`}
                justifyContent="space-between"
              >
                <ChakraText
                  lineHeight="3em"
                  fontWeight="medium"
                  fontSize="small"
                  textDecoration="underline"
                  color={colors.appClearBlue}
                >
                  <Link as={NavLink} to="/performance-alerts">
                    {t('back_to_full_url_list', '< back to full URL list')}
                  </Link>
                </ChakraText>
                <Flex>
                  <ChakraText
                    padding="0 2em"
                    lineHeight="3em"
                    fontWeight="medium"
                    cursor="pointer"
                    color={
                      deviceType === 'desktop'
                        ? colors.appBlue
                        : colors.appBattleshipGrey
                    }
                    borderBottom={
                      deviceType === 'desktop'
                        ? `1px solid ${colors.appBlue}`
                        : 'none'
                    }
                    onClick={() => void setDeviceType('desktop')}
                  >
                    {t('desktop_capitalized', 'Desktop')}
                  </ChakraText>
                  <ChakraText
                    padding="0 2em"
                    lineHeight="3em"
                    fontWeight="medium"
                    cursor="pointer"
                    color={
                      deviceType === 'mobile'
                        ? colors.appBlue
                        : colors.appBattleshipGrey
                    }
                    borderBottom={
                      deviceType === 'mobile'
                        ? `1px solid ${colors.appBlue}`
                        : 'none'
                    }
                    onClick={() => void setDeviceType('mobile')}
                  >
                    {t('mobile_capitalized', 'Mobile')}
                  </ChakraText>
                </Flex>
              </Flex>
              {filteredItems.length > 0 ? (
                <>
                  <Bar data={graphData} options={graphOptions} />

                  <Table {...getTableProps()}>
                    <Thead bgColor="appPaleGreyTwo">
                      {headerGroups.map(headerGroup => {
                        const { key, ...restHeaderGroupProps } =
                          headerGroup.getHeaderGroupProps()
                        return (
                          <Tr key={key} {...restHeaderGroupProps}>
                            {headerGroup.headers.map(column => {
                              const { key: keyCol, ...restColumn } =
                                column.getHeaderProps(
                                  column.getSortByToggleProps(),
                                )
                              return (
                                <Th
                                  key={keyCol}
                                  {...restColumn}
                                  fontSize="1em"
                                  textTransform="none"
                                  fontWeight="medium"
                                  color={colors.appDarkIndigo}
                                >
                                  <HStack>
                                    {column.render('Header')}
                                    {!column.disableSortBy &&
                                    column.id !== 'reportLink' ? (
                                      <Box
                                        as="span"
                                        pl="2"
                                        color={colors.appBattleshipGrey}
                                      >
                                        {column.isSorted ? (
                                          column.isSortedDesc ? (
                                            <Icon
                                              as={FaSortUp}
                                              aria-label={t(
                                                'sorted_descending',
                                                'sorted descending',
                                              )}
                                            />
                                          ) : (
                                            <Icon
                                              as={FaSortDown}
                                              aria-label={t(
                                                'sorted_ascending',
                                                'sorted ascending',
                                              )}
                                            />
                                          )
                                        ) : (
                                          <Icon
                                            as={FaSort}
                                            aria-label={t(
                                              'sort_by_column',
                                              'sort by column',
                                            )}
                                          />
                                        )}
                                      </Box>
                                    ) : null}
                                  </HStack>
                                </Th>
                              )
                            })}
                          </Tr>
                        )
                      })}
                    </Thead>
                    <Tbody {...getTableBodyProps()}>
                      {rows.map(row => {
                        prepareRow(row)
                        const { key, ...restRowProps } = row.getRowProps()
                        return (
                          <Tr key={key} {...restRowProps}>
                            {row.cells.map(cell => {
                              const { key: keyRow, ...restCellProps } =
                                cell.getCellProps()
                              return (
                                <Td key={keyRow} {...restCellProps}>
                                  {cell.render('Cell')}
                                </Td>
                              )
                            })}
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </>
              ) : (
                <ChakraText>
                  {t(
                    'no_summary_details_message',
                    'No saved analysis for this page yet. Check back again.',
                  )}
                </ChakraText>
              )}
            </VStack>
          ) : (
            <Center height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          )
        ) : user?.pricingPlan.isPaidPlan ? (
          <CustomPlanCTA feature={FeaturesEnum.PERFORMANCE_CENTER} />
        ) : (
          <FreePlanCTA />
        )}
      </MainContentWrapper>
    </React.Fragment>
  )
}

export default PerformanceSummaryLayout
